import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { PartyStatement } from '../../../models/ledger/subledger';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';

/**
 * Level 3 — a single party's open documents on a reconciliation account (the party statement). The
 * documents total reconciles to the party's Level-2 balance.
 */
@Component({
  selector: 'app-party-statement',
  templateUrl: './party-statement.component.html',
  styleUrl: './party-statement.component.scss',
})
export class PartyStatementComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly proxy = inject(FinancialAccountProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly isLoading = signal(0);
  readonly statement = signal<PartyStatement | null>(null);
  /** false = open items only (default); true = also show fully-paid (settled) documents. */
  readonly includeSettled = signal(false);

  readonly scopeOptions = [
    { label: this.t('COA_OpenItems', 'Open'), value: false },
    { label: this.t('COA_AllItems', 'All'), value: true },
  ];

  private accountId: string | null = null;
  private partyId: string | null = null;
  // Period-to-date (#4713): carried from the subledger so the statement shows the same fiscal month.
  private fiscalYear: number | null = null;
  private period: number | null = null;

  readonly accountName = computed(() => {
    const s = this.statement();
    return s ? this.locale.getLocalizedValue(s.accountName) : '';
  });

  private readonly syncBreadcrumb = effect(
    () => this.breadcrumbService.setCurrent(this.statement()?.partyName || this.statement()?.partyReference),
    { allowSignalWrites: true },
  );

  /** A party can hold documents in several transaction currencies; when any differ from the account's
   * functional currency, surface the functional balance column so the rows still reconcile (#4549). */
  readonly showFunctional = computed(() => {
    const s = this.statement();
    return !!s && s.documents.some((d) => d.currency && d.currency !== s.currency);
  });

  ngOnInit(): void {
    this.accountId = this.route.snapshot.queryParamMap.get('accountId');
    this.partyId = this.route.snapshot.queryParamMap.get('partyId');
    if (!this.accountId || !this.partyId) {
      this.back();
      return;
    }
    this.includeSettled.set(this.route.snapshot.queryParamMap.get('includeSettled') === 'true');
    const y = Number(this.route.snapshot.queryParamMap.get('fiscalYear'));
    const p = Number(this.route.snapshot.queryParamMap.get('period'));
    this.fiscalYear = Number.isInteger(y) && y > 0 ? y : null;
    this.period = Number.isInteger(p) && p >= 1 && p <= 12 ? p : null;
    this.load();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private load(): void {
    if (!this.accountId || !this.partyId) return;
    this.isLoading.update((v) => v + 1);
    this.proxy
      .getPartyStatement(this.accountId, this.partyId, this.fiscalYear, this.period, this.includeSettled())
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => this.statement.set(response.data ?? null),
        error: () => this.statement.set(null),
      });
  }

  onScopeChange(includeSettled: boolean): void {
    this.includeSettled.set(includeSettled);
    this.load();
  }

  statusSeverity(status: string): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'PartiallyPaid':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      case 'Open':
      case 'Posted':
        return 'info';
      default:
        return 'secondary';
    }
  }

  back(): void {
    if (this.accountId) {
      // Preserve the settled scope so returning to Level 2 keeps showing settled parties.
      this.router.navigate(['/chart-of-accounts/subledger'], {
        queryParams: {
          accountId: this.accountId,
          fiscalYear: this.fiscalYear ?? undefined,
          period: this.period ?? undefined,
          includeSettled: this.includeSettled() || undefined,
        },
      });
    } else {
      this.router.navigate(['/chart-of-accounts']);
    }
  }

  export(): void {
    const s = this.statement();
    if (!s) return;
    const header = [
      'Document',
      'Type',
      'Doc date',
      'Due date',
      'Total',
      'Paid',
      'Balance due',
      'Currency',
      `Functional balance (${s.currency})`,
      'Status',
    ];
    const rows = s.documents.map((d) => [
      d.number,
      d.documentType,
      d.documentDate ? new Date(d.documentDate).toLocaleDateString() : '',
      d.dueDate ? new Date(d.dueDate).toLocaleDateString() : '',
      d.total.toFixed(2),
      d.paid.toFixed(2),
      d.balanceDue.toFixed(2),
      d.currency,
      d.functionalBalanceDue.toFixed(2),
      d.status,
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${s.partyCode ?? 'party'}-statement.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
